import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TextField
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import FormErrorWrapper from '../common/FormErrorWrapper';

function EmailVerification() {
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState({});
  const [codeSent, setCodeSent] = useState(false);
  const [success, setSuccess] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { user, sendVerificationEmail, verifyEmail } = useAuth();

  const handleSendCode = async () => {
    setError({});
    setIsSending(true);

    try {
      await sendVerificationEmail();
      setCodeSent(true);
      // Focus on the input once code is sent
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    } catch (err) {
      setError({ non_field_errors: 'Failed to send verification code. Please try again.' });
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyCode = async () => {
    // Check if code is complete
    if (verificationCode.length !== 6) {
      setError({ code: 'Please enter the complete 6-digit verification code' });
      return;
    }

    setIsLoading(true);
    setError({});

    try {
      const response = await verifyEmail(verificationCode);

      if (response.success) {
        setSuccess(true);
        // Redirect to dashboard after successful verification
        setTimeout(() => {
          navigate('/memberships');
        }, 2000);
      }
    } catch (err) {
      setError({
        code: err.response?.data?.message || 'Invalid verification code. Please try again.'
      });
      // Clear the code input on error
      setVerificationCode('');
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (value) => {
    // Allow only digits and limit to 6 characters
    const cleaned = value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(cleaned);
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} sx={{ p: 4, mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <EmailIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />

        <Typography component="h1" variant="h5" gutterBottom>
          Verify Your Email
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: 3 }}>
          Before you can access your loyalty platform, you need to verify your email address.
        </Typography>

        {error.non_field_errors && (
          <Typography color="error" sx={{ mt: 2, mb: 2 }}>
            {error.non_field_errors}
          </Typography>
        )}

        {success ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Email Successfully Verified!
            </Typography>
            <Typography variant="body2">
              Redirecting you to the dashboard...
            </Typography>
          </Box>
        ) : (
          <>
            {!codeSent ? (
              <Box sx={{ width: '100%', mt: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleSendCode}
                  disabled={isSending}
                  sx={{ mt: 2, mb: 2 }}
                >
                  {isSending ? (
                    <CircularProgress size={24} />
                  ) : (
                    'Send Verification Code'
                  )}
                </Button>
              </Box>
            ) : (
              <Box sx={{ width: '100%', mt: 1 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  We've sent a 6-digit verification code to your email address. Enter the code below to verify your account.
                </Typography>

                <FormErrorWrapper error={error.code}>
                  <TextField
                    inputRef={inputRef}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    placeholder="000000"
                    inputProps={{
                      inputMode: 'numeric',
                      autoComplete: 'one-time-code',
                      pattern: '\\d{6}',
                      maxLength: 6,
                      style: {
                        textAlign: 'center',
                        fontSize: '2rem',
                        letterSpacing: '0.5rem',
                        fontFamily: 'monospace'
                      }
                    }}
                    value={verificationCode}
                    onChange={(e) => handleCodeChange(e.target.value)}
                    disabled={isLoading}
                    sx={{ mb: 3 }}
                  />
                </FormErrorWrapper>

                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleVerifyCode}
                  disabled={isLoading}
                  sx={{ mt: 2, mb: 2 }}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Verify Email'}
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  onClick={handleSendCode}
                  disabled={isSending}
                  sx={{ mt: 1 }}
                >
                  {isSending ? (
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                  ) : (
                    'Resend Code'
                  )}
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
}

export default EmailVerification;